import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import classnames123 from "classnames";
import { Nav, NavItem, NavLink, TabContent } from "reactstrap";

import {
  AND,
  emptyString,
  aiReplyReduxConstants,
  featureDropdownOptions,
  featureFlagsReduxConstants,
  featureFlagsTabs,
  featureFlagStatusOptions,
  featureFlagStatusConstants,
  googleSocialLinkConstants,
  googleSocialLinkStatusOptions,
  storesConstants,
} from "../../constants";
import {
  aiReplyActions,
  featureFlagsActions,
  googleSocialLinkActions,
  storeActions,
} from "../../redux/actions";

import {
  useSpinner,
  DataTable,
  updateBrandPopup,
  paginatedPostRequestBody,
  Spinner,
  ExpandableTable,
  updateFeatureFlagsPopup,
  updateGoogleSocialLinkModal,
  handleAsyncAction,
} from "../../../../shared";

import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import {
  aiReplyColumns,
  transformBrandData,
  featureFlagsColumns,
  googleSocialLinkColumns,
} from "./utility";
import FeatureFlagEditButton from "./featureFlagEditButton";
import FeatureFlagFilters from "./featureFlagFilters";
import FeatureFlagExpandedRow from "./featureFlagExpandedRow";

const ActionComponent = ({ data: { row }, onEdit }) => {
  return (
    <span className="d-flex align-items-center justify-content-start">
      <FeatureFlagEditButton
        testId="edit-icon"
        className="font-14-px pt-1 pe-1"
        onClick={() => onEdit(row.original)}
      />
    </span>
  );
};

const ActionComponentFeatureFlag = ({ data, onEdit }) => {
  return (
    <span className="d-flex align-items-center justify-content-start">
      <FeatureFlagEditButton
        testId="edit-icon"
        className="font-14-px pt-1 pe-1"
        onClick={() => onEdit(data)}
      />
    </span>
  );
};

const GoogleSocialLinkComponent = (row) => {
  return (
    <p
      key={`google-${row?.row?.id}`}
      data-type="google"
      className="m-0 text-truncate text-truncate-30ch google-link"
      title={row.getValue()}
    >
      {row.getValue()}
    </p>
  );
};

const SocialMediaLinkComponent = (row) => {
  return (
    <p
      key={`social-${row?.row?.id}`}
      data-type="social"
      className="m-0 text-truncate text-truncate-30ch"
      title={row.getValue()}
    >
      {row.getValue()}
    </p>
  );
};

const { STORE_DATA } = googleSocialLinkConstants;
const { GET_ALL_BRANDS_FOR_FEATURE_FLAGS, UPDATE_FEATURE_FLAG_FOR_BRAND } =
  featureFlagsReduxConstants;
const { GET_ALL_BRANDS_FOR_AI_REPLY, UPDATE_BRAND_SOCIAL_LINKS } =
  aiReplyReduxConstants;

const FeatureFlags = (props) => {
  const {
    getAllBrands,
    getBrandLocations,
    updateBrandLocations,
    updateBrandSocialLinks,
    getAllBrandsForAiReply,
    updateFeatureFlag,
    getAllBrandsForFeatureFlags,
    storesReducer: { allBrands },
    googleSocialLinkReducer: {
      brandLocationsData,
      brandLocationsPaginationData,
    },
    featureFlagsReducer: { featureFlagsPagination, allBrandsForFeatureFlags },
    aiReplyReducer: { aiReplyPagination, aiReplyData },
  } = props;

  const [search, setSearch] = useState(emptyString);
  const [aiReplyBrand, setAiReplyBrand] = useState(null);
  const [featureFlagBrand, setFeatureFlagBrand] = useState();
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [featureFlagStatus, setFeatureFlagStatus] = useState(null);
  const [googleSocialLinkBrand, setGoogleSocialLinkBrand] = useState(null);
  const [activeTab, setActiveTab] = useState(featureFlagsTabs.featureFlags);
  const [googleSocialLinkStatus, setGoogleSocialLinkStatus] = useState(null);
  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();
  const spinner = useSpinner();
  const FeatureFlagActionComponent = useCallback(
    (row) => {
      return (
        <ActionComponentFeatureFlag
          data={row}
          onEdit={openFeatureFlagUpdateModal}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featureFlagStatus?.value, featureFlagBrand, search, featureFlagStatus]
  );
  const AIReplyActionComponent = useCallback(
    (row) => {
      return <ActionComponent data={row} onEdit={openBrandUpdateModal} />;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aiReplyBrand, search]
  );

  const expandTableRow = ({ data }) => {
    return (
      <FeatureFlagExpandedRow
        data={data}
        onEditStore={openFeatureFlagUpdateModal}
      />
    );
  };

  const GoogleSocialLinkActionComponent = useCallback(
    (row) => {
      return (
        <ActionComponent data={row} onEdit={openUpdateGoogleSocialLinkModal} />
      );
    },
    // eslint-disable-next-line
    [
      googleSocialLinkStatus?.value,
      googleSocialLinkBrand,
      search,
      googleSocialLinkStatus,
    ]
  );

  //Render table props depends on tab
  const tableColumns = () => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        return featureFlagsColumns(FeatureFlagActionComponent);
      case featureFlagsTabs.aiReply:
        return aiReplyColumns(AIReplyActionComponent, SocialMediaLinkComponent);
      default:
        return googleSocialLinkColumns(
          GoogleSocialLinkComponent,
          GoogleSocialLinkActionComponent
        );
    }
  };

  const tableData = () => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        return allBrandsForFeatureFlags;
      case featureFlagsTabs.aiReply:
        return transformBrandData(aiReplyData);
      default:
        return brandLocationsData;
    }
  };

  const tablePageNumber = () => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        return featureFlagsPagination.number + 1;
      case featureFlagsTabs.aiReply:
        return aiReplyPagination.number + 1;
      default:
        return brandLocationsPaginationData.number + 1;
    }
  };

  const tablePageSize = () => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        return featureFlagsPagination.size;
      case featureFlagsTabs.aiReply:
        return aiReplyPagination.size;
      default:
        return brandLocationsPaginationData.size;
    }
  };

  const tableTotalElements = () => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        return featureFlagsPagination.totalElements;
      case featureFlagsTabs.aiReply:
        return aiReplyPagination.totalElements;
      default:
        return brandLocationsPaginationData.totalElements;
    }
  };

  //Toggle tab
  const toggleTab = (tab) => {
    setActiveTab(tab);
    setSearch(emptyString);
    setSorting({ id: "id", desc: true });

    switch (tab) {
      case featureFlagsTabs.featureFlags:
        setAiReplyBrand(null);
        setGoogleSocialLinkBrand(null);
        setGoogleSocialLinkStatus(null);
        break;
      case featureFlagsTabs.googleReview:
        setAiReplyBrand(null);
        setSelectedFeatures([]);
        setFeatureFlagBrand(null);
        setFeatureFlagStatus(null);
        break;
      case featureFlagsTabs.aiReply:
        setSelectedFeatures([]);
        setFeatureFlagBrand(null);
        setFeatureFlagStatus(null);
        setGoogleSocialLinkBrand(null);
        setGoogleSocialLinkStatus(null);
        break;
      default:
        break;
    }
  };

  //Open update modal for google social link
  const openUpdateGoogleSocialLinkModal = (data) => {
    setComponent(
      updateGoogleSocialLinkModal({
        spinner,
        data: data,
        onClose: closeModal,
        onConfirm: (data) => {
          handleAsyncAction(
            updateBrandLocations({
              id: data?.id,
              googleSocialLink: data?.googleSocialLink || null,
            }),
            {
              onSuccess: () => {
                closeModal();
                getGoogleSocialLinkData(0);
              },
            },
          );
        },
      })
    );
  };

  const openFeatureFlagUpdateModal = (data) => {
    setComponent(
      updateFeatureFlagsPopup({
        data: data,
        onClose: closeModal,
        onConfirm: (dataToSend, isStoreUpdate) => {
          handleAsyncAction(updateFeatureFlag(dataToSend, isStoreUpdate), {
            onSuccess: () => {
              closeModal();
              getFeatureFlagData(0);
            },
          });
        },
      })
    );
  };

  const openBrandUpdateModal = (data) => {
    setComponent(
      updateBrandPopup({
        data: data,
        onClose: closeModal,
        onConfirm: (dataToSend) => {
          handleAsyncAction(updateBrandSocialLinks(dataToSend), {
            onSuccess: () => {
              closeModal();
              getAiReplyData(0);
            },
          });
        },
      })
    );
  };

  //Handle change brand filter
  const handleBrandChange = (brand) => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        setFeatureFlagBrand(brand);
        break;
      case featureFlagsTabs.googleReview:
        setGoogleSocialLinkBrand(brand);
        break;
      case featureFlagsTabs.aiReply:
        setAiReplyBrand(brand);
        break;
      default:
        console.warn("Unhandled tab in handleBrandChange:", activeTab);
    }
  };

  //Handle change status filter
  const handleStatusChange = (status) => {
    if (!status) {
      setSelectedFeatures([]);
    }
    if (activeTab === featureFlagsTabs.featureFlags) {
      setFeatureFlagStatus(status);
    } else {
      setGoogleSocialLinkStatus(status);
    }
  };

  //Handle change feature filter
  const handleFeatureChange = (selectedOptions) => {
    setSelectedFeatures(selectedOptions);
  };

  //Generate Filter query
  const generateFilterQuery = (brandFilter, statusFilter) => {
    const brandFilterValue = `brandId = ${brandFilter?.id}`;
    if (brandFilter?.id && statusFilter?.value) {
      return `${brandFilterValue} AND ${statusFilter.value}`;
    }
    if (brandFilter?.id && !statusFilter?.value) {
      return brandFilterValue;
    }
    if (!brandFilter?.id && statusFilter?.value) {
      return statusFilter.value;
    }
    return emptyString;
  };

  const constructFeatureFlagFilter = (
    brandFilterValue,
    selectedFeatures,
    featureDropdownOptions,
    featureFlagStatus,
    featureFlagStatusConstants
  ) => {
    // Helper function to construct feature filters
    const constructFeatureFilters = (features, negate = false) =>
      features
        .map(
          (feature) =>
            `${negate ? "!" : ""}BrandFeatures.Any(it = ${feature.value})`
        )
        .join(" && ");

    // Determine the appropriate feature set based on selectedFeatures
    const features =
      selectedFeatures.length > 0 ? selectedFeatures : featureDropdownOptions;

    // Construct filters based on featureFlagStatus
    const filterByStatus = () => {
      if (featureFlagStatus?.value === featureFlagStatusConstants.ACTIVE) {
        return `(${constructFeatureFilters(features)})`;
      }
      if (featureFlagStatus?.value === featureFlagStatusConstants.INACTIVE) {
        return `(${constructFeatureFilters(features, true)})`;
      }
      return null;
    };

    // Combine brandFilterValue and the status-based filters
    const statusFilter = filterByStatus();
    return [brandFilterValue, statusFilter].filter(Boolean).join(AND);
  };

  const getFeatureFlagPaginatedPostRequestBody = (pageNumber, sortBy) => {
    // Construct brand filter
    const brandFilterValue = featureFlagBrand?.id
      ? `brandId = ${featureFlagBrand.id}`
      : emptyString;

    // Use extracted filter logic
    const filter = constructFeatureFlagFilter(
      brandFilterValue,
      selectedFeatures,
      featureDropdownOptions,
      featureFlagStatus,
      featureFlagStatusConstants
    );

    const requestBody = paginatedPostRequestBody(
      // Create request body
      featureFlagsPagination,
      pageNumber,
      search,
      sortBy,
      filter
    );

    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  const getAiReplyPaginatedPostRequestBody = (pageNumber, sortBy) => {
    // Construct brand filter
    const brandFilterValue = aiReplyBrand?.id
      ? `brandId = ${aiReplyBrand.id}`
      : emptyString;

    const requestBody = paginatedPostRequestBody(
      // Create request body
      aiReplyPagination,
      pageNumber,
      search,
      sortBy,
      brandFilterValue
    );

    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  const getFeatureFlagData = (pageNumber, sortBy) => {
    const requestBody = getFeatureFlagPaginatedPostRequestBody(
      pageNumber,
      sortBy
    );
    getAllBrandsForFeatureFlags(requestBody);
  };

  const getAiReplyData = (pageNumber, sortBy) => {
    const requestBody = getAiReplyPaginatedPostRequestBody(pageNumber, sortBy);
    getAllBrandsForAiReply(requestBody);
  };

  //Set pagination details with request body
  const getGoogleSocialLinkPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const filterQuery = generateFilterQuery(
      googleSocialLinkBrand,
      googleSocialLinkStatus
    );

    const requestBody = paginatedPostRequestBody(
      brandLocationsPaginationData,
      pageNumber,
      search,
      sortBy || sorting,
      filterQuery
    );
    if (sortBy) {
      setSorting(sortBy);
    }
    return requestBody;
  };

  //Get google social link data
  const getGoogleSocialLinkData = (pageNumber, sortBy) => {
    const requestBody = getGoogleSocialLinkPaginatedPostRequestBody(
      pageNumber,
      sortBy
    );
    getBrandLocations(requestBody);
  };

  //Get Data
  const getData = (pageNumber, sortBy) => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        getFeatureFlagData(pageNumber, sortBy);
        break;
      case featureFlagsTabs.aiReply:
        getAiReplyData(pageNumber, sortBy);
        break;
      default:
        getGoogleSocialLinkData(pageNumber, sortBy);
    }
  };

  const onSort = (sortBy) => {
    switch (activeTab) {
      case featureFlagsTabs.featureFlags:
        getFeatureFlagData(featureFlagsPagination.number, sortBy);
        break;
      case featureFlagsTabs.googleReview:
        getGoogleSocialLinkData(brandLocationsPaginationData.number, sortBy);
        break;
      case featureFlagsTabs.aiReply:
        getAiReplyData(aiReplyPagination.number, sortBy);
        break;
      default:
        break;
    }
  };

  const getTabTitle = (tab) => {
    switch (tab) {
      case featureFlagsTabs.featureFlags:
        return "Brands";
      case featureFlagsTabs.googleReview:
        return "Stores";
      case featureFlagsTabs.aiReply:
        return "AI Reply Brands";
      default:
        return "Unknown";
    }
  };

  //Get All Brands
  useEffect(() => {
    getAllBrands(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Get all data
  useEffect(() => {
    getData(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    search,
    aiReplyBrand?.id,
    featureFlagBrand?.id,
    googleSocialLinkBrand?.id,
    featureFlagStatus?.value,
    googleSocialLinkStatus?.value,
    selectedFeatures,
  ]);

  let brandValue = null;
  switch (activeTab) {
    case featureFlagsTabs.featureFlags:
      brandValue = featureFlagBrand;
      break;
    case featureFlagsTabs.googleReview:
      brandValue = googleSocialLinkBrand;
      break;
    case featureFlagsTabs.aiReply:
      brandValue = aiReplyBrand;
      break;
    default:
      brandValue = null;
  }

  return (
    <>
      {showModal && modalElement}
      <div className="container-fluid pt-2 mt-4">
        <div className="notification-form shadow card px-4 mb-3">
          <div className="card-body py-2 px-0">
            <div className="tabs tabs--bordered-bottom">
              <Nav tabs className="mb-2">
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === featureFlagsTabs.featureFlags,
                    })}
                    onClick={() => toggleTab(featureFlagsTabs.featureFlags)}
                  >
                    {featureFlagsTabs.featureFlags}
                  </NavLink>
                </NavItem>
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === featureFlagsTabs.googleReview,
                    })}
                    onClick={() => toggleTab(featureFlagsTabs.googleReview)}
                  >
                    {featureFlagsTabs.googleReview}
                  </NavLink>
                </NavItem>
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === featureFlagsTabs.aiReply,
                    })}
                    onClick={() => toggleTab(featureFlagsTabs.aiReply)}
                  >
                    {featureFlagsTabs.aiReply}
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent
                activeTab={activeTab}
                className="tab-content-diagnosis"
              >
                <FeatureFlagFilters
                  search={search}
                  activeTab={activeTab}
                  brandValue={brandValue}
                  brandOptions={allBrands}
                  brandsLoading={spinner(storesConstants.FETCH_ALL_BRANDS)}
                  statusValue={
                    activeTab === featureFlagsTabs.featureFlags
                      ? featureFlagStatus
                      : googleSocialLinkStatus
                  }
                  statusOptions={
                    activeTab === featureFlagsTabs.featureFlags
                      ? featureFlagStatusOptions
                      : googleSocialLinkStatusOptions
                  }
                  featureFlagStatus={featureFlagStatus}
                  selectedFeatures={selectedFeatures}
                  onSearchChange={(e) => setSearch(e.target.value)}
                  onSearchReset={() => setSearch(emptyString)}
                  onBrandChange={handleBrandChange}
                  onStatusChange={handleStatusChange}
                  onFeatureChange={handleFeatureChange}
                />
              </TabContent>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="notification-form card shadow">
          <div className="mx-4 py-3">
            <h5 className="mb-0">{getTabTitle(activeTab)}</h5>
          </div>
          {spinner(STORE_DATA) ||
          spinner(UPDATE_BRAND_SOCIAL_LINKS) ||
          spinner(GET_ALL_BRANDS_FOR_AI_REPLY) ||
          spinner(GET_ALL_BRANDS_FOR_FEATURE_FLAGS) ||
          spinner(UPDATE_FEATURE_FLAG_FOR_BRAND) ? (
            <Spinner />
          ) : (
            <div
              data-testid={(() => {
                switch (activeTab) {
                  case featureFlagsTabs.featureFlags:
                    return "featureFlagTable";
                  case featureFlagsTabs.googleReview:
                    return "googleSocialLinkTable";
                  case featureFlagsTabs.aiReply:
                    return "aiReplyTable";
                  default:
                    return "defaultTable";
                }
              })()}
            >
              {activeTab === featureFlagsTabs?.featureFlags ? (
                <ExpandableTable
                  columns={tableColumns()}
                  expandOnRowClicked
                  data={tableData() || []}
                  handlePageChange={(pageNum) => {
                    getData(pageNum);
                  }}
                  expandRowRenderer={expandTableRow}
                  pageNumber={tablePageNumber()}
                  totalRows={tableTotalElements()}
                  numberOfRowsPerPage={tablePageSize()}
                  enableServerSorting={true}
                />
              ) : (
                <DataTable
                  sortedBy={sorting}
                  onColumnSort={onSort}
                  data={tableData() || []}
                  columns={tableColumns()}
                  onPageChange={(pageNum) => getData(pageNum)}
                  pageNumber={tablePageNumber()}
                  totalRows={tableTotalElements()}
                  numberOfRowsPerPage={tablePageSize()}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
  aiReplyReducer: state.aiReplyReducer,
  featureFlagsReducer: state.featureFlagsReducer,
  googleSocialLinkReducer: state.googleSocialLinkReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
  getAllBrandsForFeatureFlags: (body) =>
    dispatch(featureFlagsActions.getAllBrandsForFeatureFlags(body)),
  getAllBrandsForAiReply: (body) =>
    dispatch(aiReplyActions.getAllBrandsForAiReply(body)),
  updateFeatureFlag: (body, isStoreUpdate) =>
    dispatch(featureFlagsActions.updateFeatureFlag(body, isStoreUpdate)),
  updateBrandSocialLinks: (body) =>
    dispatch(aiReplyActions.updateBrandSocialLinks(body)),
  getBrandLocations: (body) =>
    dispatch(googleSocialLinkActions.getBrandLocations(body)),
  updateBrandLocations: (rowData) =>
    dispatch(googleSocialLinkActions.updateBrandLocations(rowData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeatureFlags);
